import React, { useState } from "react";
import { Input, Button, message, Select, DatePicker } from "antd";
import ModalWrapper from "../../Modal";
import { postRequest } from "../../service/index";
import { formatDateTime } from "../../utils/dateTime";

const AddRecruitmentModal = ({ open, onRequestClose, editingData }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    interviewType: "",
    interviewDate: null,
    interviewEmail: "",
    interviewLink: "",
    reference: "",
    currentSalary: "",
    expectedSalary: "",
    // Interview fields
    interviewBy: "",
    date: null,
    remark: "",
    // Shortlist fields
    approvedBy: ""
  });
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Get the current stage from editingData
  const getCurrentStage = () => {
    if (!editingData) return null;
    return editingData.stage;
  };

  // Check if we're in view mode
  const isViewMode = () => {
    return editingData?.isViewMode || false;
  };

  // Get the data to display in view mode
  const getViewData = () => {
    if (isViewMode() && editingData?.fullData) {
      return editingData.fullData;
    }
    return editingData;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    const { fullName, phoneNumber, interviewType, interviewDate, interviewEmail } = formData;
    
    if (!fullName || !phoneNumber || !interviewType || !interviewDate || !interviewEmail) {
      message.error("Please fill in all required fields");
      return;
    }

    // Validate interview link for online interviews
    if (interviewType === "online" && !formData.interviewLink) {
      message.error("Interview link is required for online interviews");
      return;
    }

    const payload = {
      ...formData,
      interviewDate: interviewDate ? interviewDate.toISOString() : null,
      currentSalary: formData.currentSalary ? Number(formData.currentSalary) : null,
      expectedSalary: formData.expectedSalary ? Number(formData.expectedSalary) : null,
      status: "scheduled"
    };

    try {
      setLoading(true);
      const response = await postRequest("/api/onboarding", payload);
      if (response) {
        message.success("Recruitment record added successfully");
        onRequestClose();
        // Reset form
        setFormData({
          fullName: "",
          phoneNumber: "",
          interviewType: "",
          interviewDate: null,
          interviewEmail: "",
          interviewLink: "",
          reference: "",
          currentSalary: "",
          expectedSalary: ""
        });
      }
    } catch (error) {
      message.error("Failed to add recruitment record");
      console.error("Error adding recruitment record:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: "",
      phoneNumber: "",
      interviewType: "",
      interviewDate: null,
      interviewEmail: "",
      interviewLink: "",
      reference: "",
      currentSalary: "",
      expectedSalary: "",
      interviewBy: "",
      date: null,
      remark: "",
      approvedBy: ""
    });
    onRequestClose();
  };

  return (
    <ModalWrapper title={isViewMode() ? "Candidate Details - View" : "Add Recruitment"} open={open} onRequestClose={onRequestClose}>
      <div className="flex flex-col gap-4 px-2 max-h-[80vh] overflow-y-auto">
        {isViewMode() ? (
          // View mode - show all details in read-only format
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Full Name</label>
                  <p className="text-gray-900">{getViewData()?.name || getViewData()?.fullName || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Phone Number</label>
                  <p className="text-gray-900">{getViewData()?.phone || getViewData()?.phoneNumber || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Interview Type</label>
                  <p className="text-gray-900">{getViewData()?.interviewType || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Interview Date</label>
                  <p className="text-gray-900">
                    {getViewData()?.interviewDate ? formatDateTime(getViewData().interviewDate) : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Interview Email</label>
                  <p className="text-gray-900">{getViewData()?.interviewEmail || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Interview Link</label>
                  <p className="text-gray-900">
                    {getViewData()?.interviewLink ? (
                      <a href={getViewData().interviewLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {getViewData().interviewLink}
                      </a>
                    ) : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Reference</label>
                  <p className="text-gray-900">{getViewData()?.reference || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Current Salary</label>
                  <p className="text-gray-900">{getViewData()?.currentSalary ? `$${getViewData().currentSalary.toLocaleString()}` : "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Expected Salary</label>
                  <p className="text-gray-900">{getViewData()?.expectedSalary ? `$${getViewData().expectedSalary.toLocaleString()}` : "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Status</label>
                  <p className="text-gray-900 capitalize">{getViewData()?.status || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* First Interview Details */}
            {getViewData()?.firstInterview && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">First Interview Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-600">Interview By</label>
                    <p className="text-blue-900">
                      {getViewData().firstInterview.interviewBy?.firstName} {getViewData().firstInterview.interviewBy?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-600">Date</label>
                    <p className="text-blue-900">
                      {getViewData().firstInterview.date ? formatDateTime(getViewData().firstInterview.date) : "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-blue-600">Remark</label>
                    <p className="text-blue-900">{getViewData().firstInterview.remark || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Second Interview Details */}
            {getViewData()?.secondInterview && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-green-800">Second Interview Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-600">Interview By</label>
                    <p className="text-green-900">
                      {getViewData().secondInterview.interviewBy?.firstName} {getViewData().secondInterview.interviewBy?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-600">Date</label>
                    <p className="text-green-900">
                      {getViewData().secondInterview.date ? formatDateTime(getViewData().secondInterview.date) : "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-green-600">Remark</label>
                    <p className="text-green-900">{getViewData().secondInterview.remark || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Shortlist Details */}
            {getViewData()?.shortlist && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-purple-800">Shortlist Results</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-600">Approved By</label>
                    <p className="text-purple-900">
                      {getViewData().shortlist.approvedBy?.firstName} {getViewData().shortlist.approvedBy?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-600">Date</label>
                    <p className="text-purple-900">
                      {getViewData().shortlist.date ? formatDateTime(getViewData().shortlist.date) : "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-purple-600">Remark</label>
                    <p className="text-purple-900">{getViewData().shortlist.remark || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Edit mode - show form fields
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
                <label className="block mb-1">Full Name*</label>
          <Input 
                  placeholder="Enter full name" 
                  value={formData.fullName}
                  onChange={(e) => handleInputChange("fullName", e.target.value)}
          />
        </div>

        <div>
                <label className="block mb-1">Phone Number*</label>
                <Input 
                  placeholder="Enter phone number" 
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Interview Type*</label>
          <Select
                  placeholder="Select interview type"
                  value={formData.interviewType}
                  onChange={(value) => handleInputChange("interviewType", value)}
            className="w-full"
                  options={[
                    { value: "online", label: "Online" },
                    { value: "onsite", label: "Onsite" }
                  ]}
          />
        </div>

        <div>
                <label className="block mb-1">Interview Date*</label>
                <DatePicker
                  placeholder="Select interview date"
                  value={formData.interviewDate}
                  onChange={(date) => handleInputChange("interviewDate", date)}
                  className="w-full"
                  showTime
                  format="YYYY-MM-DD HH:mm"
                />
              </div>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Interview Email*</label>
                <Input 
                  type="email"
                  placeholder="Enter interview email" 
                  value={formData.interviewEmail}
                  onChange={(e) => handleInputChange("interviewEmail", e.target.value)}
                />
              </div>
              
              {formData.interviewType === "online" && (
                <div>
                  <label className="block mb-1">Interview Link*</label>
                  <Input 
                    type="url"
                    placeholder="Enter interview link (Zoom, Teams, etc.)" 
                    value={formData.interviewLink}
                    onChange={(e) => handleInputChange("interviewLink", e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Reference</label>
                <Input 
                  placeholder="Enter reference" 
                  value={formData.reference}
                  onChange={(e) => handleInputChange("reference", e.target.value)}
          />
        </div>

              <div>
                <label className="block mb-1">Current Salary</label>
                <Input 
                  type="number"
                  placeholder="Enter current salary" 
                  value={formData.currentSalary}
                  onChange={(e) => handleInputChange("currentSalary", e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block mb-1">Expected Salary</label>
              <Input 
                type="number"
                placeholder="Enter expected salary" 
                value={formData.expectedSalary}
                onChange={(e) => handleInputChange("expectedSalary", e.target.value)}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={handleCancel}>Cancel</Button>
          {!isViewMode() && (
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            className="bg-primary hover:bg-primary/80"
          >
              {editingData ? "Update" : "Save"}
          </Button>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default AddRecruitmentModal;